import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useCurrency } from "@/hooks/useUserCurrency";
import type { MonthPoint } from "@/lib/finance";

interface CashFlowChartProps {
  data: MonthPoint[];
}

const SERIES: Array<{ key: keyof MonthPoint; label: string; color: string }> = [
  { key: "loaned", label: "Emprestado (saiu)", color: "hsl(var(--destructive))" },
  { key: "received", label: "Recebido (entrou)", color: "hsl(var(--success))" },
  { key: "principal", label: "Capital devolvido", color: "hsl(var(--success))" },
  { key: "interest", label: "Juros", color: "hsl(var(--gold))" },
  { key: "penalty", label: "Multas", color: "hsl(var(--warning))" },
  { key: "profit", label: "Lucro real", color: "hsl(var(--gold))" },
  { key: "net", label: "Fluxo líquido", color: "hsl(var(--foreground))" },
];

export function CashFlowChart({ data }: CashFlowChartProps) {
  const { country, format } = useCurrency();

  const renderTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload as MonthPoint;
    return (
      <div className="rounded-xl border border-border bg-popover p-3 text-xs shadow-xl">
        <p className="mb-2 font-semibold text-foreground">{point.label ?? label}</p>
        <div className="space-y-1">
          {SERIES.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.label}
              </span>
              <span className="tabular font-medium text-foreground">
                {format(Number(point[s.key]) || 0)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const legend = [
    { label: "Emprestado (saiu)", color: "hsl(var(--destructive))" },
    { label: "Capital devolvido", color: "hsl(var(--success))" },
    { label: "Lucro (juros + multas)", color: "hsl(var(--gold))" },
    { label: "Fluxo líquido", color: "hsl(var(--foreground))" },
  ];

  return (
    <Card className="glass">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold tracking-tight">Entrou x Saiu por mês</CardTitle>
        <CardDescription className="text-xs">
          Barra vermelha: dinheiro que saiu em novos empréstimos. Barra verde: dinheiro que entrou
          (capital + lucro). Linha: saldo do mês.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} opacity={0.5} />
            <XAxis
              dataKey="month"
              stroke="hsl(var(--muted-foreground))"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="hsl(var(--muted-foreground))"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${country.currencySymbol} ${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip cursor={{ fill: "hsl(var(--primary) / 0.06)" }} content={renderTooltip} />
            <ReferenceLine y={0} stroke="hsl(var(--border))" />
            <Bar
              dataKey="loaned"
              name="Emprestado"
              fill="hsl(var(--destructive))"
              radius={[6, 6, 0, 0]}
              maxBarSize={28}
            />
            <Bar
              dataKey="principal"
              name="Capital devolvido"
              stackId="in"
              fill="hsl(var(--success))"
              maxBarSize={28}
            />
            <Bar
              dataKey="profit"
              name="Lucro"
              stackId="in"
              fill="hsl(var(--gold))"
              radius={[6, 6, 0, 0]}
              maxBarSize={28}
            />
            <Line
              type="monotone"
              dataKey="net"
              name="Fluxo líquido"
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>

        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
          {legend.map((l) => (
            <span key={l.label} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: l.color }} />
              {l.label}
            </span>
          ))}
        </div>
        <p className="mt-1.5 text-[11px] text-muted-foreground">Recebido = capital + lucro.</p>
      </CardContent>
    </Card>
  );
}
